/* eslint-disable guard-for-in */
/* eslint-disable no-restricted-syntax */
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQueries,
  useSuspenseQuery,
} from "@tanstack/react-query"
import { axiosInstance } from "../../../api/axios"
import {
  FinancialModel,
  FinancialModelSettings,
  FinancialModelSettingsStatus,
  FinancialModelSettingsV2Model,
  FinancialModelSettingsV2Response,
  FinancialModelSettingsV2Section,
  FinancialModelV2WithSettings,
  POSTFinalModelRequest,
  POSTFinalModelResponse,
} from "./types"
import { groupSettingsByStep, parseFinancialModelSettingsV2 } from "./parsers"
import {
  FINANCIAL_MODEL_SETTINGS_BASE_KEY,
  createQueryKey,
  invalidateGlobalsQueryKey,
  FINANCIAL_MODEL_BASE_KEY,
  resetGlobalQueryKey,
  TOTALS_KEY,
  PERSONNEL_BASE_KEY,
  COGS_BASE_KEY,
  OPEX_BASE_KEY,
  REVENUES_BASE_KEY,
} from "@/api/query-keys"
import { FinancialModelCardLabel } from "@/feature/financial-model/constants"
import { useFinancialModelSettingsStore } from "../settings/store/financialModelSettingsStore"

const getAllFinancialModels = async (): Promise<FinancialModel[]> => {
  const response =
    await axiosInstance.get<FinancialModel[]>(`/financial-models/`)
  return response.data
}

const getAllFinancialModelSettingsV2 = async (): Promise<
  FinancialModelV2WithSettings[]
> => {
  const financialModels = await getAllFinancialModels()
  const financialModelsSettingPromises = financialModels.map(
    async (financialModel) => {
      const financialModelSettings = await getSingleFinancialModelSettingsV2({
        id: financialModel.id,
      })
      return {
        financialModel,
        settings: financialModelSettings,
      } satisfies FinancialModelV2WithSettings
    }
  )

  const financialModelSettingsWithSettings = await Promise.all(
    financialModelsSettingPromises
  )

  return financialModelSettingsWithSettings
}

export const getAllFinancialModelsQuery = () =>
  queryOptions({
    queryKey: createQueryKey({
      baseKey: FINANCIAL_MODEL_BASE_KEY,
      keys: [`with-settings`],
    }),
    queryFn: getAllFinancialModelSettingsV2,
  })

export const useGetAllFinancialModelSettingsV2 = () =>
  useSuspenseQuery(getAllFinancialModelsQuery())

const postFinalcialModel = async (
  data: POSTFinalModelRequest
): Promise<POSTFinalModelResponse> => {
  const response = await axiosInstance.post<POSTFinalModelResponse>(
    `/financial-models/`,
    data
  )
  return response.data
}

export const usePostFinalcialModelMutation = () =>
  useMutation({
    mutationFn: postFinalcialModel,
    onSuccess: () => {
      invalidateGlobalsQueryKey([FINANCIAL_MODEL_BASE_KEY])
    },
  })

const deleteFinancialModel = async (id: string): Promise<void> => {
  await axiosInstance.delete(`/financial-models/${id}/`)
}

export const useDeleteFinancialModelMutation = () => {
  const resetFinancialModelSettings = useFinancialModelSettingsStore(
    (state) => state.resetFinancialModel
  )
  return useMutation({
    mutationFn: deleteFinancialModel,
    onSuccess: () => {
      resetFinancialModelSettings()
      invalidateGlobalsQueryKey([
        FINANCIAL_MODEL_BASE_KEY,
        FINANCIAL_MODEL_SETTINGS_BASE_KEY,
        REVENUES_BASE_KEY,
      ])
    },
  })
}

type UpdateFinancialModelParams = Pick<
  FinancialModel,
  | "name"
  | "revenue_stream_name"
  | "revenue_stream_type"
  | "id"
  | "historical_data_end_at"
  | "historical_data_start_at"
  | "forecast_data_end_at"
  | "forecast_data_start_at"
  | "functional_currency"
  | "revenue_stream_type"
  | "revenue_stream_name"
  | "fiscal_year_end"
>

const updateFinancialModel = async (
  params: Partial<UpdateFinancialModelParams>
): Promise<FinancialModel> => {
  const { id, ...data } = params
  const response = await axiosInstance.put<FinancialModel>(
    `/financial-models/${id}/`,
    data
  )
  return response.data
}

export const useUpdateFinancialModelInfoMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: updateFinancialModel,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FINANCIAL_MODEL_BASE_KEY })
    },
  })
}

export const getSingleFinancialModel = async (
  id: string
): Promise<FinancialModel> => {
  const response = await axiosInstance.get<FinancialModel>(
    `/financial-models/${id}/`
  )
  return response.data as FinancialModel
}

export const useGetSingleFinancialModelQuery = (
  id: string,
  enabled?: boolean
) =>
  useQuery({
    queryKey: createQueryKey({
      baseKey: FINANCIAL_MODEL_BASE_KEY,
      keys: [id],
    }),
    queryFn: async () => getSingleFinancialModel(id),
    enabled: !!id && !!enabled,
  })

const getSingleFinancialModelSettings = async (
  id: string
): Promise<FinancialModelSettings[]> => {
  const response = await axiosInstance.get<FinancialModelSettings[]>(
    `/financial-model-settings/${id}/`
  )
  return response.data
}

export const getSingleFinancialModelSettingsV2 = async (params: {
  id: string
  module?: FinancialModelSettingsV2Model
  section?: FinancialModelSettingsV2Section
}): Promise<FinancialModelSettingsV2Response> => {
  const response = await axiosInstance.get<FinancialModelSettingsV2Response>(
    `/financial-model-settings-v2/${params.id}/`,
    {
      params: {
        module: params.module,
        section: params.section,
      },
    }
  )
  return response.data
}

export const useGetSingleFinancialModelSettingsV2Query = (params: {
  id: string
  module?: FinancialModelSettingsV2Model
  section?: FinancialModelSettingsV2Section
}) =>
  useQuery({
    queryKey: createQueryKey({
      baseKey: FINANCIAL_MODEL_SETTINGS_BASE_KEY,
      keys: [`single`, params.id, params.module, params.section],
    }),
    queryFn: () => getSingleFinancialModelSettingsV2(params),
  })

export const useGetModuleSettingsV2Query = (params: {
  id: string
  module: FinancialModelSettingsV2Model
  section?: FinancialModelSettingsV2Section
}) =>
  useQuery({
    queryKey: createQueryKey({
      baseKey: FINANCIAL_MODEL_SETTINGS_BASE_KEY,
      keys: [`parsed`, params.id, params.module, params.section],
    }),
    queryFn: () => getSingleFinancialModelSettingsV2(params),
    select: (data) => parseFinancialModelSettingsV2(data, params.module),
    staleTime: 0,
  })

export const useGetEcommerceCompletitionStatusQuery = (
  financialModelId: string
) =>
  useQuery({
    queryKey: createQueryKey({
      baseKey: FINANCIAL_MODEL_SETTINGS_BASE_KEY,
      keys: [`ecommerce-completion-status`, financialModelId],
    }),
    queryFn: () =>
      getSingleFinancialModelSettingsV2({
        id: financialModelId,
      }),
    select: (data) => {
      const object = {} as Record<
        FinancialModelSettingsV2Model,
        {
          label: FinancialModelCardLabel
          status: number
        }
      >
      for (const key in data) {
        const element = data[key as FinancialModelSettingsV2Model]
        if (!element) {
          continue
        }
        let label = `in_progress` as FinancialModelSettingsStatus
        if (Number(element.completion_status) === 100) {
          label = `done`
        } else if (Number(element.completion_status) === 0) {
          label = `not_started`
        }
        object[key as FinancialModelSettingsV2Model] = {
          label,
          status: Number(element.completion_status),
        }
      }
      object.other_incomes_costs = {
        ...object.operating_costs,
        label: object.operating_costs.status === 0 ? `not_started` : `optional`,
      }
      return object
    },
  })

const setRevenuesModelApproach = async (
  financial_model_id: string,
  approach: string
) => {
  const response = await axiosInstance.patch(
    `/financial-model-settings/${financial_model_id}/revenue-modelling-approach`,
    {
      approach,
    }
  )
  return response.data
}

export const useSetRevenuesModelApproachMutation = (id: string) =>
  useMutation({
    mutationFn: (approach: string) => setRevenuesModelApproach(id, approach),
    onSuccess: () => {
      invalidateGlobalsQueryKey([
        FINANCIAL_MODEL_BASE_KEY,
        FINANCIAL_MODEL_SETTINGS_BASE_KEY,
      ])
      resetGlobalQueryKey([
        TOTALS_KEY,
        PERSONNEL_BASE_KEY,
        COGS_BASE_KEY,
        OPEX_BASE_KEY,
      ])
    },
  })

export const useGetDeatiledSingleFinancialModelQuery = (id: string) =>
  useSuspenseQueries({
    queries: [
      {
        queryKey: createQueryKey({
          baseKey: FINANCIAL_MODEL_BASE_KEY,
          keys: [id],
        }),
        queryFn: () => getSingleFinancialModel(id),
      },
      {
        queryKey: createQueryKey({
          baseKey: FINANCIAL_MODEL_SETTINGS_BASE_KEY,
          keys: [`single`, id],
        }),
        queryFn: () => getSingleFinancialModelSettings(id),
      },
    ],
    combine: (data) => ({
      financialModel: data[0],
      settings: data[1].data
        ? groupSettingsByStep(data[1].data)
        : ({} as Record<number, FinancialModelSettings[]>),
      isPending: data[0].isPending || data[1].isPending,
      isError: data[0].isError || data[1].isError,
      error: data[0].error || data[1].error,
    }),
  })
